/**
 * Хук для расчёта базового и связанных регионов на основе данных формы.
 * Собирает регионы из места рождения, адреса регистрации, телефона и других источников.
 */

import { useState, useCallback, useMemo } from "react";
import type { RegionAnalysis, RegionEntry } from "@/types/report";

/** Источник региона */
export type RegionSource =
  | "birth_place"
  | "registration_address"
  | `address_${number}`
  | "phone"
  | `phone_${number}`
  | "passport"
  | "tax_id"
  | "other";

/** Запись о регионе с источником */
export interface RegionRecord {
  region_name: string;
  source: RegionSource;
  source_label: string;
}

/** Маппинг базовых источников на русские названия */
const SOURCE_LABELS: Record<string, string> = {
  birth_place: "Место рождения",
  registration_address: "Адрес регистрации",
  phone: "Телефон",
  passport: "Паспорт",
  tax_id: "ИНН",
  other: "Другое",
};

/** Получить label для источника региона */
const getSourceLabel = (source: RegionSource): string => {
  if (source.startsWith("phone_")) {
    const index = parseInt(source.split("_")[1], 10);
    return index === 0 ? "Телефон" : `Телефон ${index + 1}`;
  }
  if (source.startsWith("address_")) {
    const index = parseInt(source.split("_")[1], 10);
    return index === 0 ? "Адрес" : `Адрес ${index + 1}`;
  }
  return SOURCE_LABELS[source] || "Другое";
};

/** Приоритет источников для определения базового региона */
const SOURCE_PRIORITY: RegionSource[] = [
  "registration_address",
  "address_0",
  "birth_place",
  "phone",
  "passport",
  "tax_id",
  "other",
];

interface UseRegionAnalysisReturn {
  /** Текущий анализ регионов */
  regionAnalysis: RegionAnalysis | null;
  /** Все записи о регионах */
  regionRecords: RegionRecord[];
  /** Добавить регион */
  addRegion: (regionName: string, source: RegionSource) => void;
  /** Удалить регион по источнику */
  removeRegionBySource: (source: RegionSource) => void;
  /** Очистить все регионы */
  clearRegions: () => void;
  /** Есть ли регионы */
  hasRegions: boolean;
}

/**
 * Хук для управления анализом регионов
 */
export function useRegionAnalysis(): UseRegionAnalysisReturn {
  const [regionRecords, setRegionRecords] = useState<RegionRecord[]>([]);

  /**
   * Добавить регион
   */
  const addRegion = useCallback((regionName: string, source: RegionSource) => {
    if (!regionName) return;

    setRegionRecords((prev) => {
      // Удаляем существующую запись с таким же источником
      const filtered = prev.filter((r) => r.source !== source);
      // Добавляем новую запись
      return [
        ...filtered,
        {
          region_name: regionName,
          source,
          source_label: getSourceLabel(source),
        },
      ];
    });
  }, []);

  /**
   * Удалить регион по источнику
   */
  const removeRegionBySource = useCallback((source: RegionSource) => {
    setRegionRecords((prev) => prev.filter((r) => r.source !== source));
  }, []);

  /**
   * Очистить все регионы
   */
  const clearRegions = useCallback(() => {
    setRegionRecords([]);
  }, []);

  /**
   * Вычисляем анализ регионов
   */
  const regionAnalysis = useMemo((): RegionAnalysis | null => {
    if (regionRecords.length === 0) return null;

    // Группируем записи по названию региона
    const regionMap = new Map<string, RegionRecord[]>();
    for (const record of regionRecords) {
      const existing = regionMap.get(record.region_name) || [];
      regionMap.set(record.region_name, [...existing, record]);
    }

    // Определяем базовый регион по приоритету источников
    let baseRegionName: string | null = null;
    for (const prioritySource of SOURCE_PRIORITY) {
      // Для "phone" также проверяем phone_0, phone_1, etc.
      // Для "address_0" также проверяем address_1, address_2, etc.
      const record = regionRecords.find((r) => {
        if (prioritySource === "phone") {
          return r.source === "phone" || r.source.startsWith("phone_");
        }
        if (prioritySource === "address_0") {
          return r.source.startsWith("address_");
        }
        return r.source === prioritySource;
      });
      if (record) {
        baseRegionName = record.region_name;
        break;
      }
    }

    // Если не нашли по приоритету, берём первый
    if (!baseRegionName && regionRecords.length > 0) {
      baseRegionName = regionRecords[0].region_name;
    }

    if (!baseRegionName) return null;

    // Формируем базовый регион
    const baseRegionRecords = regionMap.get(baseRegionName) || [];
    const baseRegion: RegionEntry = {
      region_name: baseRegionName,
      source_count: baseRegionRecords.length,
      sources: baseRegionRecords.map((r) => r.source_label),
    };

    // Формируем связанные регионы (все кроме базового)
    const relatedRegions: RegionEntry[] = [];
    for (const [regionName, records] of regionMap.entries()) {
      if (regionName !== baseRegionName) {
        relatedRegions.push({
          region_name: regionName,
          source_count: records.length,
          sources: records.map((r) => r.source_label),
        });
      }
    }

    // Сортируем связанные регионы по количеству источников
    relatedRegions.sort((a, b) => (b.source_count || 0) - (a.source_count || 0));

    return {
      base_region: baseRegion,
      base_region_count: baseRegionRecords.length,
      related_regions: relatedRegions,
      total_sources: regionRecords.length,
    };
  }, [regionRecords]);

  const hasRegions = regionRecords.length > 0;

  return {
    regionAnalysis,
    regionRecords,
    addRegion,
    removeRegionBySource,
    clearRegions,
    hasRegions,
  };
}
